import React, { useLayoutEffect } from "react";
import { Box, Button, Stack, Typography, CssBaseline, Alert, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Wordmark } from "./Logo";

// ─────────────────────────────────────────────────────────────────────────────
// authShared — the Fennira design for sign-in, sign-up and verify-email.
//
// The three pages used to carry three private copies of the same layout, each
// with its own blue, its own button padding and its own idea of the type scale.
// They drifted, as copies do. Everything visual now lives here; a page supplies
// only its own words and its own form.
//
// Token values come from fennira-brand.css, which mirrors the :root block of
// fennira.com's stylesheet. Nothing here hardcodes a colour.
// ─────────────────────────────────────────────────────────────────────────────

// MUI's TextField needs real overriding to stop looking like MUI.
export const fieldSx = {
  "& .MuiInputLabel-root": {
    fontFamily: "var(--fn-sans)",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--fn-muted-l)",
    "&.Mui-focused": { color: "var(--fn-teal-ink)" },
  },
  "& .MuiOutlinedInput-root": {
    fontFamily: "var(--fn-sans)",
    fontSize: 15.5,
    borderRadius: "12px",
    backgroundColor: "var(--fn-white)",
    transition: "border-color .2s var(--fn-ease)",
    "& fieldset": { borderColor: "var(--fn-line-l)", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "var(--fn-navy-3)" },
    "&.Mui-focused fieldset": { borderColor: "var(--fn-teal)", borderWidth: "1.5px" },
  },
  "& .MuiOutlinedInput-input": { padding: "15px 14px" },
};

// .fn-btn from the site: pill, 54px tall, weight 700, no shadow, 0.98 on press.
const btnBase = {
  minHeight: 54,
  borderRadius: "999px",
  fontFamily: "var(--fn-sans)",
  fontWeight: 700,
  fontSize: 16,
  textTransform: "none",
  boxShadow: "none",
  transition: "background .25s var(--fn-ease), color .25s var(--fn-ease), border-color .25s var(--fn-ease), transform .25s var(--fn-ease)",
  "&:active": { transform: "scale(.98)" },
  "&:hover": { boxShadow: "none" },
};

// .fn-btn--ink
export function PrimaryButton({ children, sx = {}, ...rest }) {
  return (
    <Button
      fullWidth
      {...rest}
      sx={{
        ...btnBase,
        bgcolor: "var(--fn-navy)",
        color: "var(--fn-cream)",
        "&:hover": { ...btnBase["&:hover"], bgcolor: "var(--fn-ink)" },
        "&.Mui-disabled": { bgcolor: "var(--fn-navy)", color: "var(--fn-cream)", opacity: .6 },
        "& .MuiButton-endIcon": { transition: "transform .25s var(--fn-ease)" },
        "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

// .fn-btn--line — outline that fills in on hover.
export function GoogleButton({ sx = {}, ...rest }) {
  return (
    <Button
      fullWidth
      startIcon={<GoogleGlyph />}
      {...rest}
      sx={{
        ...btnBase,
        bgcolor: "transparent",
        color: "var(--fn-navy)",
        border: "1.5px solid var(--fn-navy)",
        "&:hover": { ...btnBase["&:hover"], bgcolor: "var(--fn-navy)", color: "var(--fn-cream)" },
        ...sx,
      }}
    >
      Continue with Google
    </Button>
  );
}

// The same outline button without Google's mark: secondary actions on the
// sign-in pages ("Create an account", "Use a recovery code").
export function LineButton({ children, sx = {}, ...rest }) {
  return (
    <Button
      fullWidth
      {...rest}
      sx={{
        ...btnBase,
        bgcolor: "transparent",
        color: "var(--fn-navy)",
        border: "1.5px solid var(--fn-navy)",
        "&:hover": { ...btnBase["&:hover"], bgcolor: "var(--fn-navy)", color: "var(--fn-cream)" },
        "&.Mui-disabled": { color: "var(--fn-navy)", opacity: .5 },
        ...sx,
      }}
    >
      {children}
    </Button>
  );
}

// A quiet text link styled like the site's.
export const linkSx = {
  color: "var(--fn-teal-ink)", fontWeight: 700, cursor: "pointer",
  textDecorationColor: "rgba(11,107,98,.35)",
  "&:hover": { textDecorationColor: "currentColor" },
};

/** Terms · Privacy, small and muted, under the sign-in and sign-up forms. */
export function LegalLinks({ sx = {} }) {
  const s = { color: "var(--fn-muted-l)", textDecorationColor: "rgba(85,82,122,.35)", "&:hover": { color: "var(--fn-navy)" } };
  return (
    <Typography sx={{ mt: 4, fontSize: 13.5, color: "var(--fn-muted-l)", textAlign: "center", ...sx }}>
      <Link component={RouterLink} to="/terms" sx={s}>Terms</Link>
      <Box component="span" aria-hidden sx={{ mx: 1.25 }}>·</Box>
      <Link component={RouterLink} to="/privacy" sx={s}>Privacy</Link>
    </Typography>
  );
}

// .fn-label — mono, uppercase, wide tracking, with a small square before it.
export function Eyebrow({ children, tone = "light" }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: "10px",
        fontFamily: "var(--fn-mono)", fontSize: 13, letterSpacing: ".08em",
        textTransform: "uppercase",
        color: tone === "dark" ? "var(--fn-teal)" : "var(--fn-teal-ink)",
        "&::before": {
          content: '""', width: 8, height: 8, borderRadius: "2px",
          backgroundColor: "currentColor",
        },
      }}
    >
      {children}
    </Box>
  );
}

export function Heading({ children, sx = {} }) {
  return (
    <Typography
      component="h1"
      sx={{
        fontFamily: "var(--fn-display)",
        fontWeight: 800,
        fontSize: "clamp(38px, 4.4vw, 54px)",
        lineHeight: 1.0,
        letterSpacing: "-.028em",
        mt: 2.5,
        mb: 1.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// One notice component for both outcomes. Severity drives the colour so a
// success and a failure can never be mistaken for one another, which the old
// verify page could do — it printed both in the same plain grey text.
export function Notice({ severity = "error", onClose, children }) {
  const ok = severity === "success";
  return (
    <Alert
      severity={severity}
      onClose={onClose}
      sx={{
        mb: 2.5, borderRadius: "12px",
        fontFamily: "var(--fn-sans)", fontSize: 14,
        bgcolor: ok ? "rgba(24,165,152,.10)" : "rgba(229,72,77,.08)",
        color: ok ? "var(--fn-teal-ink)" : "#8E2429",
        border: `1px solid ${ok ? "rgba(24,165,152,.3)" : "rgba(229,72,77,.25)"}`,
        "& .MuiAlert-icon": { color: ok ? "var(--fn-teal)" : "var(--fn-red)" },
      }}
    >
      {children}
    </Alert>
  );
}

// ── The two-panel shell ──────────────────────────────────────────────────────
// `panel` is the ink column on the right: it disappears below md, where the
// form takes the whole screen rather than being squeezed beside a decoration.
export function AuthLayout({ panel, maxWidth = 430, children }) {
  // Opts these pages out of App.css's old ≤1024px rules (every Stack forced
  // into a column, every button full width), like the Studio does. The layout
  // here never relied on them, and they broke rows such as Copy / Download.
  useLayoutEffect(() => {
    document.body.classList.add("fn-studio");
    return () => document.body.classList.remove("fn-studio");
  }, []);

  return (
    <>
      <CssBaseline />
      <Box
        className="fn-auth"
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          fontFamily: "var(--fn-sans)",
          color: "var(--fn-navy)",
        }}
      >
        <Box
          sx={{
            flex: "1 1 50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "var(--fn-paper)",
            px: { xs: 3, sm: 6 },
            py: { xs: 5, md: 8 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth }}>
            <Wordmark on="light" height={32} sx={{ mb: { xs: 5, md: 7 } }} />
            {children}
          </Box>
        </Box>

        <Box
          sx={{
            flex: "1 1 50%",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            bgcolor: "var(--fn-ink)",
            color: "var(--fn-cream)",
            px: 8,
          }}
        >
          {/* The soft navy circle bleeding off-canvas, as on the site's panels. */}
          <Box
            aria-hidden
            sx={{
              position: "absolute", right: "-22%", top: "-18%",
              width: "78%", aspectRatio: "1", borderRadius: "50%",
              background: "var(--fn-navy-2)", opacity: .5,
              filter: "blur(2px)", pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", maxWidth: 480 }}>
            <Eyebrow tone="dark">{panel.eyebrow}</Eyebrow>
            <Typography
              sx={{
                fontFamily: "var(--fn-display)",
                fontWeight: 800,
                fontSize: "clamp(44px, 4.6vw, 68px)",
                lineHeight: .98,
                letterSpacing: "-.032em",
                mt: 2.5,
                textWrap: "balance",
              }}
            >
              {panel.heading}
            </Typography>
            <Typography sx={{ color: "var(--fn-muted-d)", fontSize: 17, mt: 3, maxWidth: 420 }}>
              {panel.body}
            </Typography>
            {panel.extra}
          </Box>
        </Box>
      </Box>
    </>
  );
}

// ── The control-room card: the site's visual language, with live-looking
//    readouts in JetBrains Mono. Static by design — it is decoration on a
//    logged-out page, so it polls nothing and costs nothing.
export function StatusCard() {
  return (
    <Box
      sx={{
        mt: 5, p: 2.5,
        bgcolor: "var(--fn-navy)",
        border: "1px solid var(--fn-line-d)",
        borderRadius: "16px",
        maxWidth: 420,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            bgcolor: "var(--fn-red)", color: "#fff",
            px: 1.25, py: .625, borderRadius: "6px",
            fontSize: 12, fontWeight: 700, letterSpacing: ".04em",
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#fff", animation: "fn-pulse 1.6s ease-in-out infinite" }} />
          ON AIR
        </Box>

        <Stack direction="row" spacing={.5} alignItems="flex-end" sx={{ height: 18 }}>
          {[0, .15, .3, .45, .6].map((d) => (
            <Box
              key={d}
              sx={{
                width: 3, height: 6, borderRadius: "2px", bgcolor: "var(--fn-teal)",
                animation: "fn-eq .9s ease-in-out infinite alternate",
                animationDelay: `${d}s`,
              }}
            />
          ))}
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 13, color: "var(--fn-cream)" }}>
          42:10
        </Typography>
      </Stack>

      <Stack direction="row" spacing={3} sx={{ mt: 2.25 }}>
        {[["BITRATE", "5.9 Mbps"], ["FRAMERATE", "30 fps"], ["OUTPUTS", "6 live"]].map(([k, v]) => (
          <Box key={k}>
            <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 10.5, letterSpacing: ".06em", color: "var(--fn-muted-d)" }}>
              {k}
            </Typography>
            <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 15, fontWeight: 500, color: "var(--fn-cream)", mt: .25 }}>
              {v}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// The destination list, for the sign-up panel: what you get by registering.
export function DestinationList() {
  const rows = [
    ["YT", "YouTube"], ["f", "Facebook"], ["in", "LinkedIn"],
    ["X", "X"], ["K", "Kick"], ["↗", "Any RTMP"],
  ];
  return (
    <Box sx={{ mt: 5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, maxWidth: 420 }}>
      {rows.map(([badge, name]) => (
        <Stack
          key={name} direction="row" alignItems="center" spacing={1.25}
          sx={{
            p: 1.25, borderRadius: "12px",
            bgcolor: "var(--fn-navy)", border: "1px solid var(--fn-line-d)",
          }}
        >
          <Box
            sx={{
              width: 28, height: 28, flex: "none", borderRadius: "8px",
              bgcolor: "var(--fn-cream)", color: "var(--fn-navy)",
              display: "grid", placeItems: "center",
              font: "700 12px/1 var(--fn-sans)",
            }}
          >
            {badge}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--fn-cream)" }}>
            {name}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

// Google's mark, inline so the button does not wait on a third-party request.
export function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.0 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.0 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C39.1 35.8 44 30.5 44 24c0-1.3-.1-2.6-.4-3.9z"/>
    </svg>
  );
}
